// =====================================================================
// aiCreditPreflight — pure helpers used by the Composer pre-flight modal
// (`useCreditPreflight` hook). The hook owns dialog state; this module
// owns the data-fetch + decision logic so it stays unit-testable.
//
// The hard cap is enforced server-side in the AI router. This is a
// courtesy check that warns the user before kicking off a job that's
// almost certainly going to bounce.
// =====================================================================

import { supabase } from "@/integrations/supabase/client";
import { estimateCreditsForJob, type AiJobKind } from "@/lib/aiCreditEstimates";

export type PreflightStats = {
  monthly_credits_cap: number | null;
  monthly_credits_used: number;
  topup_credits_balance: number;
  tier: string;
  byok_active: boolean;
};

export type PreflightDecision =
  | { ok: true; reason: "byok" | "sufficient" | "no-stats" }
  | {
      ok: false;
      reason: "shortfall";
      stats: PreflightStats;
      estimate: number;
      remainingMonthly: number;
      totalAvailable: number;
    };

export const JOB_LABELS: Record<AiJobKind, string> = {
  brief_draft: "Writing Agent",
  brief_outline: "Outline brief",
  excerpt_assist: "Excerpt helper",
  composer_chat_turn: "AI Chat turn",
};

export const labelForJob = (job: AiJobKind): string => JOB_LABELS[job] ?? job;

/** Pure decision helper — no network, no DOM. Easy to unit-test. */
export const decidePreflight = (
  stats: PreflightStats | null,
  job: AiJobKind,
): PreflightDecision => {
  if (!stats) return { ok: true, reason: "no-stats" };

  // BYOK tiers with an active key bypass the credit pool entirely.
  if ((stats.tier === "elite" || stats.tier === "agency") && stats.byok_active) {
    return { ok: true, reason: "byok" };
  }

  const cap = stats.monthly_credits_cap;
  const remainingMonthly =
    cap === null ? Number.POSITIVE_INFINITY : Math.max(0, cap - stats.monthly_credits_used);
  const totalAvailable =
    remainingMonthly === Number.POSITIVE_INFINITY
      ? Number.POSITIVE_INFINITY
      : remainingMonthly + stats.topup_credits_balance;

  const estimate = estimateCreditsForJob(job);
  if (totalAvailable >= estimate) return { ok: true, reason: "sufficient" };

  return {
    ok: false,
    reason: "shortfall",
    stats,
    estimate,
    remainingMonthly: remainingMonthly === Number.POSITIVE_INFINITY ? -1 : remainingMonthly,
    totalAvailable: totalAvailable === Number.POSITIVE_INFINITY ? -1 : totalAvailable,
  };
};

/**
 * Session-scoped "skip-this-job" cache. Keyed by `${workspaceId}:${jobKind}`.
 * Resets on full page reload.
 */
const skipCache = new Set<string>();
const skipKey = (workspaceId: string, job: AiJobKind) => `${workspaceId}:${job}`;
export const isPreflightSkipped = (workspaceId: string, job: AiJobKind) =>
  skipCache.has(skipKey(workspaceId, job));
export const markPreflightSkipped = (workspaceId: string, job: AiJobKind) => {
  skipCache.add(skipKey(workspaceId, job));
};
/** Test-only helper. */
export const __resetPreflightSkipCache = () => skipCache.clear();

/** Fetch the cached account-ai-usage stats. Returns null on any error. */
export const fetchPreflightStats = async (
  workspaceId: string,
): Promise<PreflightStats | null> => {
  try {
    const { data, error } = await supabase.functions.invoke("account-ai-usage", {
      body: { workspace_id: workspaceId },
    });
    if (error || data?.error) return null;
    return data as PreflightStats;
  } catch {
    return null;
  }
};
